import clsx from 'clsx';
import React, { useCallback, useState } from 'react';

import giftBoxIcon from 'images/gift/giftbox_icon.svg';

import {
  new_session_path,
  referrals_path,
  subscribe_path,
  subscribe_promotion_path,
} from '../../../routes';
import { gift_landing_path } from '../../../routes';
import { READ_ONLY_MODE } from '../../../src/env';
import { NavCurrentUser } from '../../../utils/backend-api/currentUser';
import { Button } from '../../BaseUI';
import ReferralModal from '../../Referral/Modal';
import AvatarDropdown from '../AvatarDropdown';
import sideNavStyles from '../SideNav/styles.module.scss';
import styles from './styles.module.scss';

type variant = 'top' | 'side';

export default function SubscriberActions({
  colorScheme,
  currentUser,
  dropdownShown,
  overrideOfferCode,
  setDropdownShown,
  shouldOpenReferralModal,
  variant,
}: {
  colorScheme?: string;
  currentUser: NavCurrentUser;
  dropdownShown: string;
  overrideOfferCode?: string;
  setDropdownShown: React.Dispatch<React.SetStateAction<string>>;
  shouldOpenReferralModal: boolean;
  variant: variant;
}) {
  const inTopNav = variant === 'top';
  const inSideNav = variant === 'side';
  const subscribePath = overrideOfferCode
    ? subscribe_promotion_path({ offer: overrideOfferCode })
    : subscribe_path();
  const [referralModalIsOpen, setReferralModalIsOpen] = useState(false);

  const subscriberCtaJSX = useCallback(() => {
    // TODO: Update this to actual pro landing page path once we have it
    if (!currentUser.isProSubscriber && !currentUser.willSubscriptionChange) {
      return (
        <Button variant="logo" rounded="full" href={'/pro'}>
          {inTopNav ? 'Upgrade to Pro' : 'Get Pro'}
        </Button>
      );
    }

    const action = shouldOpenReferralModal
      ? { onClick: () => setReferralModalIsOpen(!referralModalIsOpen) }
      : { href: referrals_path() };
    return (
      <Button variant="logo" rounded="full" {...action}>
        {inTopNav ? 'Invite Friends' : 'Invite'}
      </Button>
    );
  }, [currentUser, inTopNav, referralModalIsOpen, shouldOpenReferralModal]);

  return (
    <div
      className={clsx({
        [styles.topNavWrapper]: inTopNav,
        [styles.sideNavWrapper]: inSideNav,
        [sideNavStyles.sideNavSection]: inSideNav,
      })}
    >
      <ReferralModal
        isOpen={referralModalIsOpen}
        onClose={() => setReferralModalIsOpen(false)}
        referralUrl={currentUser.referralUrl}
        currentUserPlanType={currentUser.planType}
      />
      {currentUser.isLoggedIn ? (
        <>
          {currentUser.isSubscriber ? (
            !READ_ONLY_MODE && (
              <>
                {currentUser.canViewGiftOption && (
                  <a
                    href={gift_landing_path()}
                    className={clsx(styles.giftIconLink, { [styles.hideOnMedium]: inTopNav })}
                  >
                    <img src={giftBoxIcon} alt="giftIcon" />
                  </a>
                )}
                <div
                  className={clsx(styles.firstActionWrapper, { [styles.hideOnMedium]: inTopNav })}
                >
                  {subscriberCtaJSX()}
                </div>
              </>
            )
          ) : (
            <div className={clsx(styles.firstActionWrapper, { [styles.hideOnMobile]: inTopNav })}>
              <Button variant="logo" rounded="full" href={subscribePath}>
                Subscribe
              </Button>
            </div>
          )}
          {inTopNav && (
            <AvatarDropdown
              avatarUrl={currentUser.avatarUrl}
              profileCompletionPercentage={currentUser.profileCompletionPercentage}
              profileCompletionStatuses={currentUser.profileCompletionStatuses}
              username={currentUser.username}
              dropdownShown={dropdownShown}
              initialSubscribeDateStr={currentUser.initialSubscribeDateStr}
              setDropdownShown={setDropdownShown}
            />
          )}
        </>
      ) : (
        <div
          className={clsx({
            [styles.notLoggedInWrapper]: inTopNav,
            [styles.hideOnMobile]: inTopNav,
            [styles.notLoggedInSideNavSection]: inSideNav,
            [sideNavStyles.sideNavSection]: inSideNav,
          })}
        >
          <div className={styles.firstActionWrapper}>
            <a
              className={clsx(styles.altNavLink, {
                [styles.sideNavAltNavLink]: inSideNav,
                [styles.darkThemeLink]: colorScheme === 'dark',
              })}
              href={new_session_path()}
            >
              Sign In
            </a>
          </div>
          <div>
            <Button variant="logo" rounded="full" href={subscribePath}>
              Subscribe
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
